'use strict';

import { Component } from 'react';
import { connectStateResults, Highlight } from 'react-instantsearch-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faHourglassHalf } from '@fortawesome/pro-regular-svg-icons/faHourglassHalf';
import { CourseStatus, courseStatusFor } from './course-status';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle';

class Course extends Component {
	state = {
		expanded: false,
	};
	
	render() {
		const { hit } = this.props;
		const bullet_points = this.bulletPoints();
		const approvals = this.approvals();
		
		return (
			<div className="overflow-hidden p-5 mb-4 space-y-4 bg-white rounded-lg border-2">
				<div className="grid gap-y-1 gap-x-2 items-center sm:items-start grid-cols-[min-content_1fr] sm:grid-cols-[1fr_min-content]">
					<h2 className="col-span-full m-0 text-lg sm:col-span-1">
						<a href={ hit.marketing_url } target="_blank">
							<Highlight attribute="name" hit={ hit } />
						</a>
					</h2>
					<span className="whitespace-nowrap pill pill-cyan">{ hit.category }</span>
					{ this.statusBadge() }
				</div>
				<p>
					<Highlight attribute="description" hit={ hit } />
				</p>
				{ bullet_points }
				{ approvals }
			</div>
		);
	}
	
	statusBadge() {
		const status = courseStatusFor(this.props.hit.objectID);
		
		if (! status) {
			return null;
		}
		
		const { icon, label, classes } = CourseStatus.Completed === status
			? { icon: faCheckCircle, label: 'You’ve completed this course', classes: 'text-green-600' }
			: { icon: faHourglassHalf, label: 'You’ve started progress on this course', classes: 'text-yellow-700' };
		
		return (
			<div className="flex gap-1.5 text-sm leading-none text-gray-700 font-condensed">
				<Icon icon={ icon } className={ `-mt-0.5 size-4 ${ classes }` } /> { label }
			</div>
		);
	}
	
	bulletPoints() {
		const { hit } = this.props;
		
		const bullet_points = ['Free for InterNACHI members'];
		
		const bulletCondition = (condition, if_true, if_false) => {
			if (condition) {
				return bullet_points.push(if_true);
			}
			if (if_false) {
				bullet_points.push(if_false);
			}
		};
		
		bulletCondition(hit.available_to_non_members, 'Free for non-members', 'Available to non-members for a fee');
		bulletCondition(hit.credit_hours, `${ hit.credit_hours } InterNACHI credit hour${ 1 === hit.credit_hours ? '' : 's' }`);
		bulletCondition(hit.cpi_requirement, 'Required for Certified Professional Inspector certification');
		bulletCondition(hit.section_count > 2, `${ hit.section_count } section${ 1 === hit.section_count ? '' : 's' }`);
		bulletCondition(hit.quiz_count > 1, `${ hit.quiz_count } quizzes`);
		bulletCondition(hit.question_count, `${ hit.question_count }-question final exam`);
		bulletCondition(hit.language && hit.language !== 'English', hit.language);
		bulletCondition(hit.region && hit.region !== 'No Specific Region', `Specific to ${ hit.region }`);
		bulletCondition('Inspectors' !== hit.audience, `For ${ hit.audience }`);
		
		return (
			<ul>
				{ bullet_points.map(point => <li key={ point }>{ point }</li>) }
			</ul>
		);
	}
	
	approvals() {
		const { hit, searchState } = this.props;
		
		if (! hit.approvals || ! hit.approvals.length) {
			return null;
		}
		
		let searched_approval_levels = '[intentionally does not match anything]';
		if (`hierarchicalMenu` in searchState && `approval_levels.lvl0` in searchState.hierarchicalMenu) {
			searched_approval_levels = searchState.hierarchicalMenu[`approval_levels.lvl0`];
		}
		
		const matched_approvals = hit.approvals.filter(approval => {
			return 'approval_level_path' in approval
				&& approval.approval_level_path.startsWith(searched_approval_levels);
		});
		
		const unmatched_approvals = hit.approvals.filter(approval => {
			return ! ('approval_level_path' in approval)
				|| ! approval.approval_level_path.startsWith(searched_approval_levels);
		});
		
		if (! this.state.expanded && ! matched_approvals.length) {
			return (
				<button type="button" onClick={ e => this.expand(e) } className="text-xs link">
					Show { hit.approvals.length } course approval{ hit.approvals.length === 1 ? '' : 's' }
				</button>
			);
		}
		
		const displayApproval = approval => {
			let hours = '';
			let license = '';
			
			if (hit.credit_hours !== approval.credit_hours) {
				hours = ` (${ approval.credit_hours } hour${ 1 === approval.credit_hours ? '' : 's' })`;
			} else {
				hours = ` (${ hit.credit_hours } hour${ 1 === hit.credit_hours ? '' : 's' })`;
			}
			
			if (approval.license_number) {
				license = ` (Course #${ approval.license_number })`;
			}
			
			const label = `${ approval.name }${ hours } ${ license }`;
			
			if (window.adminAreaLinks && window.adminAreaLinks[approval.administrative_area]) {
				return (
					<li key={ approval.name }>
						<a href={ window.adminAreaLinks[approval.administrative_area] } target="_blank">
							{ label }
						</a>
					</li>
				);
			}
			
			if ('Certified Commercial Property Inspectors Association (CCPIA)' === label) {
				return (
					<li key={ approval.name }>
						<a href="https://ccpia.org/" target="_blank">
							{ label }
						</a>
					</li>
				);
			}
			
			return <li key={ approval.name }>{ label }</li>;
		};
		
		return (
			<div className="mt-4 space-y-3">
				<p>
					<strong>Approved by:</strong>
					{ this.state.expanded && (
						<button type="button" className="ml-1 text-xs link" onClick={ e => this.contract(e) }>
							(hide course approval{ unmatched_approvals.length === 1 ? '' : 's' })
						</button>
					) }
				</p>
				<ul>
					{ matched_approvals.map(displayApproval) }
					
					{ this.state.expanded && unmatched_approvals.map(displayApproval) }
				</ul>
				{ ! this.state.expanded && (
					<button type="button" onClick={ e => this.expand(e) } className="text-xs link">
						Show { unmatched_approvals.length } additional course approval{ unmatched_approvals.length === 1 ? '' : 's' }
					</button>
				) }
				{ this.state.expanded && (
					<button type="button" onClick={ e => this.contract(e) } className="text-xs link">
						Hide course approval{ unmatched_approvals.length === 1 ? '' : 's' }
					</button>
				) }
			</div>
		);
	}
	
	expand(e) {
		e.preventDefault();
		this.setState({ expanded: true });
	}
	
	contract(e) {
		e.preventDefault();
		this.setState({ expanded: false });
	}
}

export default connectStateResults(Course);
